import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FormDecidir } from './components/FormDecidir';
import { usePaymentTitle } from './hooks/usePaymentTitle';

import { Button, Container, Loader, Modal, NavBar } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import ROUTES from '@/router/routes';
import { formatNumberToLocale, PACKS_BY_ID, PACKS_BY_NAME } from '@/utils';

export const PaymentForm = () => {
  const navigate = useNavigate();
  const { isDesktop } = useIsDesktop();
  const [isLoaded, setIsLoaded] = useState(false);
  const title = usePaymentTitle();
  const { updatePinsData, pinsData } = usePinsAccount();
  const [state, setState] = useState({ isSuccess: false, error: false });

  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, [isLoaded]);

  const txtBaseSucess = (txtProduct: string) => {
    return (
      <>
        ¡Tu {txtProduct} se
        <br />
        realizó de manera exitosa!
      </>
    );
  };
  const txtResponse = useMemo(() => {
    // EXITO
    if (state.isSuccess) {
      if (pinsData.moneyToWallet) {
        return txtBaseSucess(
          `Carga de Saldo $${formatNumberToLocale(pinsData.moneyToWallet)}`
        );
      } else if (
        pinsData.selectedProduct &&
        [
          PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_1],
          PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_2],
          PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_3],
          [PACKS_BY_ID[PACKS_BY_NAME.PIN_FECHA_ORO]],
        ].includes(pinsData.selectedProduct.id)
      ) {
        return txtBaseSucess(`compra de 1 Pin Especial TDA por Premios`);
      } else if (
        pinsData.selectedProduct &&
        [PACKS_BY_ID[PACKS_BY_NAME.PIN_FECHA_ORO]].includes(
          pinsData.selectedProduct.id
        )
      ) {
        return txtBaseSucess(`compra de 1 Pin Especial Fecha de Oro`);
      } else if (
        pinsData.selectedProduct &&
        [
          PACKS_BY_ID[PACKS_BY_NAME.PREMIUM],
          PACKS_BY_ID[PACKS_BY_NAME.PREMIUM_1],
        ].includes(pinsData.selectedProduct.id)
      ) {
        return txtBaseSucess(`compra de Pack Premium`);
      } else {
        return txtBaseSucess(
          `compra de ${
            pinsData.selectedProduct?.cantidad === 1
              ? '1 pin'
              : `${pinsData.selectedProduct?.cantidad} pines`
          }`
        );
      }
    } else {
      // ERROR
      return (
        <>
          Tu transacción no pudo realizarse. Intentalo de nuevo o comunicate al{' '}
          <Button href="tel:+5491152891740" type="link" variant="text">
            5289-1740
          </Button>
        </>
      );
    }
  }, [pinsData.moneyToWallet, pinsData.selectedProduct, state]);

  const amount = (() => {
    if (pinsData.selectedInstallment) {
      return pinsData.selectedInstallment.totalAmount;
    }
    return pinsData.selectedProduct?.precio ?? pinsData.moneyToWallet ?? 0;
  })();

  const resetStateMyAccount = () => {
    updatePinsData({
      ...pinsData,
      selectedProduct: undefined,
      selectedCard: undefined,
      showWalletLoadModal: false,
      showModalWallet: false,
      showPaymentModalMethod: false,
      selectedPaymentMethod: undefined,
      moneyToWallet: undefined,
    });
  };

  window.onpopstate = () => {
    isDesktop && resetStateMyAccount();
  };

  const handleRedirect = () => {
    if (pinsData && pinsData.selectedProduct && pinsData.selectedProduct.id) {
      switch (pinsData.selectedProduct.id) {
        case 30:
        case 31:
        case 32:
          navigate(ROUTES.friendsTournamentPrizes);
          break;

        default:
          navigate(ROUTES.myAccount, { replace: true });
          break;
      }
    } else {
      navigate(ROUTES.myAccount, { replace: true });
    }
    resetStateMyAccount();
  };

  return (
    <>
      <Loader isVisible={!isLoaded} />
      <NavBar
        desktop
        heightAuto="auto"
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.selectPaymentCard, {
                replace: true,
              });
            },
          },
          centerContent: {
            title,
          },
        }}
      >
        <Container className="flex relative" variant="fluid">
          <>
            <FormDecidir
              amount={amount}
              cardType={pinsData.selectedCard ?? 'CREDITO'}
              installments={pinsData.selectedInstallment?.quantity}
              labelTokenTarjeta={
                pinsData.tokenizedCard?.lastFourDigits
                  ? `**** **** **** ${pinsData.tokenizedCard?.lastFourDigits}`
                  : ''
              }
              productId={pinsData.selectedProduct?.id}
              tokenTarjeta={pinsData.tokenizedCard?.token || ''}
              onFinish={(success: boolean) => {
                setState({
                  isSuccess: success,
                  error: !success,
                });
              }}
            />
            <Modal
              // eslint-disable-next-line no-console
              closeOutside={false}
              controlledOnClose={() => null}
              isControlledOpen={state.isSuccess || state.error}
              render={() => {
                return (
                  <>
                    <p className="text-center mt-3 mb-8">{txtResponse}</p>
                    <Button
                      className="w-full mb-4"
                      type="button"
                      variant="primary"
                      onClick={() => handleRedirect()}
                    >
                      Aceptar
                    </Button>
                  </>
                );
              }}
              showCloseButton={false}
              size="xs"
              title={state.isSuccess ? 'Pago confirmado' : 'Pago sin realizar'}
              variant="controlled"
            />
          </>
        </Container>
      </NavBar>
    </>
  );
};
